import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('zentara_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem('zentara_onboarding_complete') === 'true';
  });

  // Fetch current user on mount if token exists
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          localStorage.setItem('zentara_user', JSON.stringify(userData));
          // Check if user has seen onboarding from server
          if (userData.has_seen_onboarding) {
            setHasSeenOnboarding(true);
            localStorage.setItem('zentara_onboarding_complete', 'true');
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('zentara_token');
          localStorage.removeItem('zentara_user');
          setToken(null);
        }
      } catch (err) {
        console.error('Auth fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const text = await res.text();
      let data;
      
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse login response:', text);
        throw new Error('Invalid server response');
      }

      if (!res.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      localStorage.setItem('zentara_token', data.access_token);
      localStorage.setItem('zentara_user', JSON.stringify(data.user));
      setToken(data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      localStorage.setItem('zentara_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('zentara_token');
    localStorage.removeItem('zentara_user');
    localStorage.removeItem('zentara_onboarding_complete');
    setToken(null);
    setUser(null);
    setHasSeenOnboarding(false);
  }, []);

  // Mark onboarding as complete
  const completeOnboarding = useCallback(async () => {
    setHasSeenOnboarding(true);
    localStorage.setItem('zentara_onboarding_complete', 'true');
    
    // Also save to server
    if (token) {
      try {
        await fetch(`${API_URL}/api/users/me/onboarding-complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Failed to save onboarding status:', err);
      }
    }
  }, [token]);

  // Role-based permission helpers
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isEditor = user?.role === 'editor';
  const isViewer = user?.role === 'viewer';
  const isMaintenanceTech = user?.role === 'maintenance_tech';
  const canEdit = isAdmin || isSuperAdmin || isEditor;  // Admin and editor can edit
  const canView = !!user;  // All authenticated users can view
  const canLogMaintenance = !!user;  // All authenticated users can log maintenance

  // Client type helpers
  const clientType = user?.client_type;
  const isCommercialIndustrial = clientType === 'commercial_industrial';
  const isSurplusEquipment = clientType === 'surplus_equipment';
  const isTrainingClient = clientType === 'training';
  const isSatoCanada = clientType === 'sato_canada';
  
  // Feature access based on client type
  // Commercial/Industrial: All features
  // Surplus Equipment: Only Tags and Guided Tours
  // Training & Sato Canada: Training features (quizzes, hotspots, modules)
  const hasFullFeatures = isAdmin || isCommercialIndustrial || !clientType;
  const hasLimitedFeatures = isSurplusEquipment;
  const hasTrainingFeatures = isTrainingClient || isSatoCanada;

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    hasSeenOnboarding,
    // Role helpers
    isAdmin,
    isSuperAdmin,
    isEditor,
    isViewer,
    isMaintenanceTech,
    canEdit,
    canView,
    canLogMaintenance,
    // Client helpers
    clientType,
    isCommercialIndustrial,
    isSurplusEquipment,
    isTrainingClient,
    isSatoCanada,
    hasFullFeatures,
    hasLimitedFeatures,
    hasTrainingFeatures,
    login,
    register,
    logout,
    completeOnboarding
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
